/**
 * PartnerNotifications — bell icon + dropdown panel for partner dashboards.
 *
 * Backed by the new endpoints:
 *   GET    /api/partner/notifications
 *   PUT    /api/partner/notifications/:id/read
 *   PUT    /api/partner/notifications/read-all
 *
 * Surfaces:
 *   • New leads assigned to me
 *   • Lead stage changes
 *   • Asset publish / AI bundle match results
 *
 * The component is intentionally self-contained: drop it into the partner
 * nav of any dashboard and it'll fetch on mount + every 60s.
 */
import { useEffect, useState, useRef } from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';
import axios from 'axios';
import { getAuthToken } from '@/utils/auth';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tone = (type) => {
  if (type === 'new_lead') return 'bg-emerald-500/15 text-emerald-300';
  if (type === 'lead_stage_changed') return 'bg-sky-500/15 text-sky-300';
  if (type === 'asset_matched' || type === 'bundle_match') return 'bg-gold/15 text-gold';
  return 'bg-white/10 text-white';
};

export const PartnerNotifications = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const fetchAll = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const { data } = await axios.get(`${API}/partner/notifications?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      // silent — bell is a non-critical surface
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 60_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (open && popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markOne = async (id) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API}/partner/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {
      toast.error('Could not mark as read');
    }
  };

  const markAll = async () => {
    try {
      const token = getAuthToken();
      const { data } = await axios.put(`${API}/partner/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
      toast.success(`${data.marked} notification${data.marked !== 1 ? 's' : ''} marked as read`);
    } catch {
      toast.error('Could not mark all as read');
    }
  };

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Notifications"
        data-testid="partner-notifications-bell"
      >
        <Bell className="w-5 h-5 text-text-primary" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-black text-[10px] font-bold flex items-center justify-center ring-2 ring-[#0a0a0a]"
            data-testid="partner-notifications-unread-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-[360px] max-w-[90vw] glass-card border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
          data-testid="partner-notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div>
              <div className="text-sm font-semibold">Notifications</div>
              <div className="text-[11px] text-text-secondary">{unread} unread</div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={markAll}
                className="text-xs h-7 px-2 text-text-secondary hover:text-gold"
                disabled={unread === 0}
                data-testid="partner-notifications-mark-all"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all
              </Button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md hover:bg-white/10"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-text-secondary" />
              </button>
            </div>
          </div>

          <div className="max-h-[440px] overflow-y-auto divide-y divide-white/5">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-text-secondary">
                You're all caught up.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.is_read && markOne(n.id)}
                className={`w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors ${
                  n.is_read ? 'opacity-60' : ''
                }`}
                data-testid={`partner-notification-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-0.5 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium ${tone(n.type)}`}
                  >
                    {(n.type || 'notice').replace(/_/g, ' ')}
                  </span>
                  {!n.is_read && (
                    <span className="ml-auto w-2 h-2 mt-1.5 rounded-full bg-gold flex-shrink-0" />
                  )}
                </div>
                <div className="mt-1 text-sm font-medium text-text-primary leading-snug">
                  {n.title}
                </div>
                <div className="text-xs text-text-secondary mt-0.5 line-clamp-2">
                  {n.message}
                </div>
                <div className="text-[10px] text-text-secondary/70 mt-1.5">
                  {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerNotifications;
